import React, { useMemo, useState } from 'react';
import { Alert, Badge, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { compareRows, getPagedRows, getPageSummary } from './salesPageUtils';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeUnit = (unit = {}) => ({
  ...unit,
  id: Number(getSalesId(unit) || 0),
  modelNumber: String(getSalesValue(unit, 'modelNumber', 'ModelNumber', 'productTypeId', 'ProductTypeId', 'stationId', 'StationId') || ''),
  description: `Station ${getSalesValue(unit, 'stationId', 'StationId') || '-'} / Configuration ${getSalesValue(unit, 'configurationId', 'ConfigurationId') || '-'}`,
  stationType: String(getSalesValue(unit, 'stationId', 'StationId') || ''),
  configuration: String(getSalesValue(unit, 'configurationId', 'ConfigurationId') || ''),
  maxFlow: Number(getSalesValue(unit, 'totalGPM', 'TotalGPM') || 0),
  maxPressure: Number(getSalesValue(unit, 'disChargePSI', 'DisChargePSI') || 0),
  isActive: !Boolean(getSalesValue(unit, 'deleted', 'Deleted') ?? false),
  sortOrder: Number(getSalesId(unit) || 0),
});

const normalizeUnits = (rows) => rows.map(normalizeUnit);

const UnitsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const { items: units, loading, error } = useSalesApiList((endpoints) => endpoints.stationTypes.base, {
    normalize: normalizeUnits,
    errorMessage: 'Unable to load units.',
  });

  const sortedUnits = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const filtered = units.filter((unit) =>
      [unit.id, unit.modelNumber, unit.description, unit.stationType, unit.configuration, unit.maxFlow, unit.maxPressure]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );

    return [...filtered].sort((left, right) => compareRows(left, right, sortField, sortDirection));
  }, [searchTerm, sortDirection, sortField, units]);

  const pagedUnits = getPagedRows(sortedUnits, currentPage, rowsPerPage);
  const { firstRecord, lastRecord, totalPages } = getPageSummary(sortedUnits.length, currentPage, rowsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up ms-2"></i>;
    return sortDirection === 'asc' ? <i className="bi bi-arrow-up ms-2"></i> : <i className="bi bi-arrow-down ms-2"></i>;
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-units-title">
      <Row className="mb-3">
        <Col><h4 id="sales-units-title">Units List</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control
                type="text"
                placeholder="Search units..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Link to="/sales/units/new" className="btn btn-success addbtnview">
            <i className="bi bi-plus-circle me-2"></i>
            Add New Unit
          </Link>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} style={{ cursor: 'pointer', width: '80px' }}>ID {getSortIcon('id')}</th>
                  <th onClick={() => handleSort('modelNumber')} style={{ cursor: 'pointer', width: '150px' }}>Model/Type {getSortIcon('modelNumber')}</th>
                  <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>Description {getSortIcon('description')}</th>
                  <th onClick={() => handleSort('stationType')} style={{ cursor: 'pointer', width: '180px' }}>Station {getSortIcon('stationType')}</th>
                  <th onClick={() => handleSort('configuration')} style={{ cursor: 'pointer', width: '120px' }}>Config {getSortIcon('configuration')}</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Max Flow</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Max PSI</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading units...</div></td></tr>
                ) : pagedUnits.length === 0 ? (
                  <tr>
                    <td colSpan={9}><div className="sales-no-data">{searchTerm ? 'No units match your search' : 'No units found'}</div></td>
                  </tr>
                ) : (
                  pagedUnits.map((unit) => (
                    <tr key={unit.id}>
                      <td style={{ textAlign: 'center' }}><Badge bg="secondary">{unit.id}</Badge></td>
                      <td><strong>{unit.modelNumber || '-'}</strong></td>
                      <td>{unit.description}</td>
                      <td>{unit.stationType || '-'}</td>
                      <td><Badge bg="info">{unit.configuration || '-'}</Badge></td>
                      <td style={{ textAlign: 'center' }}>{unit.maxFlow || '-'} GPM</td>
                      <td style={{ textAlign: 'center' }}>{unit.maxPressure || '-'} PSI</td>
                      <td style={{ textAlign: 'center' }}>
                        <Badge bg={unit.isActive ? 'success' : 'secondary'}>{unit.isActive ? 'Active' : 'Inactive'}</Badge>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Link to={`/sales/units/${unit.id}`} className="edit-icon" title="View/Edit">
                          <i className="bi bi-pencil-square"></i>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <div className="mt-3 d-flex align-items-center justify-content-between text-muted small">
        <span>
          <i className="bi bi-info-circle me-2"></i>
          Showing {firstRecord}-{lastRecord} of {sortedUnits.length} units from the live Sales API.
        </span>
        <div className="d-flex align-items-center gap-2">
          <Form.Select size="sm" value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setCurrentPage(1); }} style={{ width: 90 }}>
            {[10, 25, 50, 100].map((value) => <option key={value} value={value}>{value}</option>)}
          </Form.Select>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((page) => page + 1)}>Next</button>
        </div>
      </div>
    </section>
  );
};

export default UnitsList;
